import { taskLabel } from './workUtils';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  AlertTriangle, Building2, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Info, Loader2,
  Mail, Upload, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Hint } from '@/components/Hint';
import { statusPillClass, type StatusTone } from '@/lib/status';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  createVendorDraft,
  deleteOrderTask,
  uploadOrderDocument,
  type OpenAgentTask,
  type OrderDocHint,
} from '@/api/orders';
import { Can } from '@/auth/guards';
import { ACOT_NEW_SUPPLIER_URL, acotReceivingUrl, fmtDate } from './lib';

// ---------------------------------------------------------------------------
// Shared furniture for the WORK views. Every view is a queue: optional
// toolbar, then sections of rows, each row carrying its action inline and
// pointing back at its journey. The primitives here are the ONLY way those
// parts render, so the eleven queues divide, tint, empty and fail the same
// way. Geometry is the utilities on each primitive; sibling spacing keys off
// the data-work-* markers (toolbar, notice, table).
// ---------------------------------------------------------------------------

/** The tones a row, a table row or a notice can carry: the app's one status
 *  vocabulary (lib/status.ts), so a tone means the same thing here as
 *  on a pill. */
export type WorkTone = StatusTone;

/** A toned row: the 3px status edge plus a faint wash, as on a late calendar
 *  chip. Shared by WorkRow and WorkTable rows so the two never drift. */
const ROW_TONE = 'data-[tone=info]:bg-info-surface/45 data-[tone=info]:shadow-[inset_3px_0_0_var(--tone-info)] data-[tone=warning]:bg-warning-surface/45 data-[tone=warning]:shadow-[inset_3px_0_0_var(--tone-warning)] data-[tone=danger]:bg-danger-surface/45 data-[tone=danger]:shadow-[inset_3px_0_0_var(--tone-danger)] data-[tone=success]:bg-success-surface/45 data-[tone=success]:shadow-[inset_3px_0_0_var(--tone-success)]';

/**
 * The card a work queue renders in — table type, header ground, control
 * sizes. Shared by every work view (WorkViewPage) and the warehouse page,
 * which carry their own margins. Carries `data-queue-content` at the call site
 * (scroll restoration finds the card by it).
 */
export const QUEUE_CARD_CLASS = 'overflow-clip rounded-[10px] border border-border bg-card px-(--queue-pad) shadow-card [--queue-pad:20px] max-md:[--queue-pad:16px] max-md:[--work-toolbar-h:0px]! [&_table]:text-[13px] [&_th]:bg-muted [&_th]:font-medium [&_th]:text-muted-foreground [&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5 **:data-[slot=button]:min-h-8 **:data-[slot=button]:rounded-md pointer-coarse:**:data-[slot=button]:min-h-11 pointer-coarse:**:data-[slot=button]:min-w-11 [&_[data-slot=input]]:min-h-[34px] [&_[data-slot=select-trigger]]:min-h-[34px] [&_[data-queue]]:min-w-0';

/**
 * One queue row, two lines: `title` (the journey or item the row is about) on
 * top, the facts that bear on the work underneath, the action cluster centred
 * on the right. The leading slot renders only when a row has something to put
 * in it (the expand chevron, or a spacer passed as `lead` so a non-expandable
 * row lines up with its expandable neighbours) — otherwise the title starts at
 * the card's edge. `onToggle`
 * makes the whole line the expander; the action cluster swallows its own
 * clicks so a button there never toggles the row. `detail` renders under the
 * line — an expanded panel, an inline form, a note — indented to the title.
 */
export function WorkRow({ scrollKey, title, lead, tone, expanded, onToggle, actions, detail, className, children }: {
  /** The scroll-restoration anchor — every row has one. */
  scrollKey: string;
  /** First line: the journey name, or the item when the row is about a product. */
  title: React.ReactNode;
  /** Glyph or spacer in the leading slot. Replaced by the chevron when `onToggle` is set. */
  lead?: React.ReactNode;
  /**
   * The row's own state, only when it sets the row apart from its neighbours
   * (a delivery problem, a flagged line, a document only you can source). A
   * section whose every row shares a state says so in its title, not on each
   * row; a date's urgency lives on its pill.
   */
  tone?: WorkTone;
  expanded?: boolean;
  onToggle?: () => void;
  /** Right-aligned cluster of RowActions / chips. */
  actions?: React.ReactNode;
  /** Rendered under the line, indented to the title column. */
  detail?: React.ReactNode;
  className?: string;
  /** Second line: chips, dates, amounts — only what the work needs. */
  children?: React.ReactNode;
}) {
  const toggle = onToggle
    ? {
      role: 'button' as const,
      tabIndex: 0,
      'aria-expanded': Boolean(expanded),
      onClick: onToggle,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
      },
    }
    : {};
  const hasLead = Boolean(onToggle) || lead != null;
  return (
    <div
      className={cn(
        '-mx-(--queue-pad) border-b border-border/60 px-(--queue-pad) py-3 last:border-b-0',
        ROW_TONE,
        className,
      )}
      data-scroll-key={scrollKey}
      data-tone={tone}
    >
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-3 gap-y-2',
          onToggle && '-mx-(--queue-pad) -my-3 cursor-pointer px-(--queue-pad) py-3 hover:bg-accent',
        )}
        {...toggle}
      >
        {hasLead && (
          <span className="inline-flex w-4 shrink-0 items-center justify-center text-muted-foreground [&>svg]:size-3.5" aria-hidden={onToggle ? undefined : true}>
            {onToggle
              ? (expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)
              : lead}
          </span>
        )}
        <span className="flex min-w-0 flex-[1_1_20rem] flex-col gap-1">
          <span className="text-[14px] leading-[1.35] [&_[data-journey-vendor]]:font-semibold">{title}</span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground empty:hidden [&_.text-sm]:text-foreground">{children}</span>
        </span>
        {actions && (
          <span className="ml-auto inline-flex shrink-0 flex-wrap items-center justify-end gap-1.5 self-center" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            {actions}
          </span>
        )}
      </div>
      {detail && <div className={cn(onToggle ? 'mt-[18px]' : 'mt-3.5', hasLead && 'pl-7')}>{detail}</div>}
    </div>
  );
}

/**
 * A titled run of rows. The band is sticky and full-bleed (see
 * .work-section-title) so 28 rows under one heading never scroll away from the
 * label that explains them. A section has a title and a count and nothing
 * else to say — what a queue holds is the page header's About popover.
 * `actions` sits at the band's right edge (a group's "Open journey").
 */
export function WorkSection({ title, count, actions, className, children }: {
  title: React.ReactNode;
  count?: number;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn('mt-6 first:mt-0 [[data-work-notice]+&]:mt-3 [[data-work-toolbar]+&]:mt-0', className)}>
      <WorkSectionTitle count={count} actions={actions}>{title}</WorkSectionTitle>
      {children}
    </section>
  );
}

export function WorkSectionTitle({ children, count, actions }: {
  children: React.ReactNode;
  count?: number;
  actions?: React.ReactNode;
}) {
  return (
    <div className="sticky top-[var(--work-toolbar-h,0px)] z-10 -mx-(--queue-pad) mb-1 flex flex-wrap items-center gap-2.5 border-y border-border bg-muted px-(--queue-pad) py-[9px] [[data-work-toolbar]+section>&]:border-t-0 [section:first-child>&]:border-t-0">
      <span className="inline-flex flex-wrap items-center gap-2 font-display text-[15px] font-bold tracking-[-.01em] text-foreground">{children}</span>
      {count != null && <span className="inline-flex h-5 min-w-[22px] items-center justify-center rounded-full border border-border bg-card px-[7px] font-display text-[12px] font-bold tabular-nums text-muted-foreground">{count.toLocaleString()}</span>}
      {actions && <span className="ml-auto inline-flex items-center gap-1.5">{actions}</span>}
    </div>
  );
}

/**
 * The card's top band: filters and their summary count. Same class as the
 * journey list's toolbar so the two surfaces read as one, and sticky so a
 * long table never scrolls its own filters away. It measures itself so the
 * sticky section bands below it stack under it instead of behind it. On a
 * phone the wrapped toolbar is too tall to pin, so it scrolls away and the
 * card zeroes the offset (WorkViewPage).
 */
export function WorkToolbar({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const card = el?.closest<HTMLElement>('[data-queue-content]');
    if (!el || !card) return;
    const apply = () => card.style.setProperty('--work-toolbar-h', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => { ro.disconnect(); card.style.removeProperty('--work-toolbar-h'); };
  }, []);
  return (
    <div
      ref={ref}
      data-work-toolbar=""
      className={cn(
        'sticky top-0 z-[11] max-md:static -mx-(--queue-pad) flex flex-wrap items-center gap-2.5 border-b border-border bg-card px-5 py-3.5 max-md:p-3 [&_input]:min-w-0 max-md:[&>input]:w-full max-md:[&>input]:flex-[1_1_100%] [&+*]:mt-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** The pill segmented control both table views switch on. */
export function WorkSegmented<T extends string>({ value, options, onChange, label }: {
  value: T;
  options: { value: T; label: React.ReactNode }[];
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-full border border-border text-[12px]" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          className="px-3 py-1.5 text-muted-foreground hover:text-foreground aria-pressed:bg-primary/10 aria-pressed:font-medium aria-pressed:text-primary"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The one table. Three queues (Move, Discrepancies, Price review) list
 * products rather than journeys, and each carried its own thead colours,
 * cell padding and border classes — most of them overridden by the card's CSS
 * anyway. Geometry lives here: full-bleed like the rows, muted head,
 * the same hairline between rows. Cells are plain <th>/<td>; add `className=
 * "num"` for right-aligned tabular figures and `data-tone` on a <tr> for the
 * row tints WorkRow uses.
 */
export function WorkTable({ cols, minWidth, head, children, className }: {
  /** One entry per column: a Tailwind width class, or undefined for the flex column. */
  cols: (string | undefined)[];
  /** Below this the wrapper scrolls sideways instead of crushing the columns. */
  minWidth?: string;
  /** The <th> cells of the single header row. */
  head: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div data-work-table="" className={cn('-mx-(--queue-pad) overflow-x-auto [[data-work-table]+&]:mt-6 [[data-work-toolbar]+&]:mt-0', className)}>
      <table
        style={minWidth ? { minWidth } : undefined}
        className={cn(
          'w-full table-fixed border-collapse text-[13px]',
          '[&_thead_th]:whitespace-nowrap [&_thead_th]:border-b [&_thead_th]:border-border [&_thead_th]:bg-muted [&_thead_th]:px-3 [&_thead_th]:py-2 [&_thead_th]:text-left [&_thead_th]:text-[12px] [&_thead_th]:font-medium [&_thead_th]:text-muted-foreground',
          '[&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2.5 [&_td]:align-top',
          '[&_th:first-child]:pl-(--queue-pad) [&_td:first-child]:pl-(--queue-pad) [&_th:last-child]:pr-(--queue-pad) [&_td:last-child]:pr-(--queue-pad)',
          '[&_.num]:text-right [&_.num]:tabular-nums',
          // The WorkRow tone on a table row: wash on every cell, edge on the first.
          '[&_tr[data-tone=danger]>td]:bg-danger-surface/45 [&_tr[data-tone=warning]>td]:bg-warning-surface/45 [&_tr[data-tone=info]>td]:bg-info-surface/45 [&_tr[data-tone=success]>td]:bg-success-surface/45',
          '[&_tr[data-tone=danger]>td:first-child]:shadow-[inset_3px_0_0_var(--tone-danger)] [&_tr[data-tone=warning]>td:first-child]:shadow-[inset_3px_0_0_var(--tone-warning)] [&_tr[data-tone=info]>td:first-child]:shadow-[inset_3px_0_0_var(--tone-info)] [&_tr[data-tone=success]>td:first-child]:shadow-[inset_3px_0_0_var(--tone-success)]',
          '[[data-work-table]:last-child>&_tr:last-child>td]:border-b-0',
        )}
      >
        <colgroup>
          {cols.map((c, i) => <col key={i} className={c} />)}
        </colgroup>
        <thead><tr>{head}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/**
 * One notice shape for every banner a view raises: a failed side-read, an
 * explainer over a list, a "with the run, nothing to do here". Danger
 * announces (role=alert); the rest are polite status.
 */
export function WorkNotice({ tone = 'info', icon, onRetry, children, className }: {
  tone?: WorkTone;
  icon?: React.ReactNode;
  onRetry?: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  const glyph = icon ?? (tone === 'danger' || tone === 'warning'
    ? <AlertTriangle className="h-3.5 w-3.5" />
    : tone === 'success' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Info className="h-3.5 w-3.5" />);
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-work-notice=""
      className={cn(
        'my-3 flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-[12px] leading-[1.5] [&_[data-slot=button]]:self-center [[data-work-notice]+&]:-mt-1',
        'data-[tone=neutral]:border-border data-[tone=neutral]:bg-muted data-[tone=neutral]:text-muted-foreground data-[tone=info]:border-info-line data-[tone=info]:bg-info-surface data-[tone=info]:text-info-ink data-[tone=warning]:border-warning-line data-[tone=warning]:bg-warning-surface data-[tone=warning]:text-warning-ink data-[tone=danger]:border-danger-line data-[tone=danger]:bg-danger-surface data-[tone=danger]:text-danger-ink data-[tone=success]:border-success-line data-[tone=success]:bg-success-surface data-[tone=success]:text-success-ink',
        className,
      )}
      data-tone={tone}
    >
      <span className="mt-0.5 inline-flex shrink-0">{glyph}</span>
      <span className="min-w-0 flex-1">{children}</span>
      {onRetry && <RowAction onClick={onRetry}>Retry</RowAction>}
    </div>
  );
}

/** A view's own loading state — for the reads it makes beside the overview. */
export function WorkLoading({ children }: { children?: React.ReactNode }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 px-6 py-10 text-[13px] text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> {children ?? 'Loading…'}
    </div>
  );
}

/** The quiet last line: what is settled, routed elsewhere, or already done. */
export function WorkFootnote({ children }: { children: React.ReactNode }) {
  return <p className="py-3.5 text-[12px] text-muted-foreground">{children}</p>;
}

/**
 * Nothing here. Full-size for an empty view; `compact` inside a section that
 * has siblings, where the 48px version would out-weigh the rows around it.
 */
export function WorkEmpty({ icon, compact, detail, children }: {
  icon?: React.ReactNode;
  compact?: boolean;
  /** Longer copy or a control under the headline. */
  detail?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 px-6 py-12 text-center text-[14px] text-muted-foreground [&>svg]:size-8',
        compact && 'gap-2 px-6 py-5 text-[13px] [&>svg]:size-5',
      )}
    >
      {/* The green check means "all clear"; a caller's own icon (no matches,
          nothing kept) is a plain fact and stays muted. */}
      {icon ?? <CheckCircle2 className="text-success-ink" />}
      <p>{children}</p>
      {detail && <div className="max-w-[48ch] text-[12px]">{detail}</div>}
    </div>
  );
}

/**
 * The one way a row action renders, everywhere: primary = outline, secondary =
 * ghost, quiet = ghost in muted ink, danger = quiet that turns red on hover.
 * Views were split between shadcn buttons, hand-rolled pill-chips and bare
 * text links for the same weight of action — this is the merge point.
 */
export function RowAction({ variant = 'primary', busy, icon, children, className, ...props }: {
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger';
  busy?: boolean;
  icon?: React.ReactNode;
} & Omit<React.ComponentProps<typeof Button>, 'variant'>) {
  return (
    <Button
      size="sm"
      variant={variant === 'primary' ? 'outline' : 'ghost'}
      className={cn(
        'h-8 gap-1.5 px-3 text-xs',
        variant === 'quiet' && 'text-muted-foreground hover:text-foreground',
        variant === 'danger' && 'text-muted-foreground hover:text-danger-ink',
        className
      )}
      {...props}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : icon}
      {children}
    </Button>
  );
}

/**
 * Ask the vendor by email: drafts an unsent reply in Mail on the journey's
 * vendor contact message and opens it in a new tab — the one way the app
 * emails a vendor. With no vendor email linked there is nothing to reply on,
 * and the button says so.
 */
export function VendorDraftButton({ orderId, vendorEmail, purpose, text, label, variant = 'quiet' }: {
  orderId: string;
  vendorEmail: string | null;
  purpose: 'document' | 'claim';
  text: string;
  label: string;
  variant?: 'primary' | 'quiet';
}) {
  const [busy, setBusy] = useState(false);
  const draft = async () => {
    // Opened inside the click so the browser allows it; pointed at the draft
    // once it exists.
    const tab = window.open('about:blank', '_blank');
    setBusy(true);
    try {
      const r = await createVendorDraft(orderId, { purpose, text });
      if (tab) tab.location.href = r.open_url;
      else window.open(r.open_url, '_blank');
      toast.success(`Draft to ${r.to} is in Mail, not sent`);
    } catch (error) {
      tab?.close();
      toast.error(error instanceof Error ? error.message : 'Could not create the draft');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Hint content={vendorEmail
      ? `Draft a reply to ${vendorEmail} in Mail (not sent)`
      : 'No vendor email is linked to this journey'}
    >
      <span>
        <RowAction
          variant={variant}
          icon={<Mail className="h-3 w-3" />}
          busy={busy}
          disabled={busy || !vendorEmail}
          onClick={draft}
        >
          {label}
        </RowAction>
      </span>
    </Hint>
  );
}

/**
 * A receiving id as THE link to its acot receiving-edit grid — where moves and
 * cost edits actually happen. One implementation for every view: three views
 * carried their own copy while two others showed the id as dead text, which is
 * how the same number was a link in Balance and an inert label in Duties.
 */
export function AcotReceivingLink({ receivingId, vendor, className }: {
  receivingId: number;
  vendor?: string | null;
  className?: string;
}) {
  return (
    <Hint content={`Open receiving ${receivingId} in the acot backend`}>
      <a
        href={acotReceivingUrl(receivingId)}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:underline',
          className
        )}
      >
        #{receivingId}{vendor ? ` ${vendor}` : ''} <ExternalLink className="h-3 w-3" />
      </a>
    </Hint>
  );
}

/** Vendor + label + "open journey" in one breath — the row's identity line.
 *  Pass `min-w-0 truncate` (plus a max-w) via className when the row must not
 *  wrap — long labels otherwise shove their neighbours onto a second line. */
export function JourneyName({ vendor, label, onOpen, className }: {
  vendor: string;
  label: string | null;
  onOpen?: () => void;
  className?: string;
}) {
  const name = (
    <>
      <span data-journey-vendor="" className="font-medium">{vendor}</span>
      {label && <span className="text-muted-foreground"> — {label}</span>}
    </>
  );
  if (!onOpen) return <span className={cn('text-sm', className)}>{name}</span>;
  return (
    <Hint content="Open journey">
      <button
        type="button"
        className={cn('text-left text-sm hover:underline', className)}
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
      >
        {name}
      </button>
    </Hint>
  );
}


/**
 * The honest interim state: a nudge is recorded, the derived fact catches up
 * next run. Rendered wherever the nudged action's button would otherwise be,
 * with take-back — a nudge the run has not consumed can always be withdrawn.
 */
export function PendingNudge({ task, onWithdrawn }: {
  task: OpenAgentTask | { id: string; kind: string; createdAt: string };
  onWithdrawn: () => void;
}) {
  const [busy, setBusy] = useState(false);
  // Always `info`: a nudge is with the run now — someone else has it.
  return (
    <span className={statusPillClass('info')}>
      {taskLabel(task.kind)}
      <span className="opacity-70">{fmtDate(task.createdAt)}</span>
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Can code="orders:write:nudges">
          <Hint content="Withdraw this nudge (the run has not picked it up yet)">
            <button
              type="button"
              className="opacity-60 hover:opacity-100"
              onClick={async (e) => {
                e.stopPropagation();
                setBusy(true);
                try { await deleteOrderTask(task.id); onWithdrawn(); }
                finally { setBusy(false); }
              }}
            >
              <X className="h-3 w-3" />
            </button>
          </Hint>
        </Can>
      )}
    </span>
  );
}

/**
 * "Upload file" as one self-contained row action: picks, uploads, reports. The
 * file lands immediately; parsing happens next run via the process_document
 * task the upload queues — the button's job ends at the honest "uploaded" state.
 */
export function UploadDocumentButton({ orderId, docHint, label, onUploaded, className }: {
  orderId: string;
  docHint: OrderDocHint;
  label?: string;
  onUploaded: () => void;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    // POST /api/orders/:id/documents is gated on orders:write:documents server-side, so a
    // read-only user is never shown the picker.
    <Can code="orders:write:documents">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setBusy(true);
          setError(null);
          try {
            await uploadOrderDocument(orderId, file, { docHint });
            onUploaded();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Upload failed');
          } finally {
            setBusy(false);
          }
        }}
      />
      <Hint content={error ?? 'Upload a file straight onto this journey — it lands now, the next run parses it'}>
        <RowAction
          variant="secondary"
          busy={busy}
          disabled={busy}
          icon={<Upload className="h-3 w-3" />}
          className={cn(error ? 'text-danger-ink' : 'text-muted-foreground hover:text-foreground', className)}
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
        >
          {label ?? 'Upload file'}
        </RowAction>
      </Hint>
    </Can>
  );
}

/**
 * The new-supplier chip: opens the backend's create-supplier page AND copies
 * the journey's vendor name in the same click, so the first field is a paste.
 * Children override the default label (the journey-row flag passes its
 * responsive icon/text pair).
 */
export function NewSupplierLink({ vendor, className, children }: {
  vendor: string;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <Hint content={`Create the supplier account — opens the backend and copies "${vendor}" to paste in`}>
      <a
        href={ACOT_NEW_SUPPLIER_URL}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => {
          e.stopPropagation();
          // Fire-and-forget: the navigation is the action; a clipboard denial
          // (permissions, non-secure context) must not break it.
          void navigator.clipboard?.writeText(vendor).catch(() => {});
        }}
        // A gap only you can close (there is no supplier yet): `warning`.
        className={cn(statusPillClass('warning'), 'hover:underline', className)}
      >
        {children ?? (
          <>
            <Building2 className="h-3 w-3" /> new supplier <ExternalLink className="h-2.5 w-2.5" />
          </>
        )}
      </a>
    </Hint>
  );
}
